import React, { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import {
  getCorrectDateTimeString,
} from "../../utils/chartUtils";
// import useToast from '../../hooks/useToast';
import { listNotesApi } from "../../HTTPS/ApiService";
import { usePatient } from "../../context/PatientContext";
import { useUser } from "../../context/UserContext";
import SearchBar from "../UI/SearchBar";
// import NoteBox from '../NoteBox';
import Observation from "../UI/Observation";
import ReuseableBlurModel from "../Modals/ReuseableBlurModel";
import EditObservationForm from "../Forms/EditObservationForm";
import AddObservationForm from "../Forms/AddObservationForm";

function ObservationBlock({ observationsRef }) {
  const [editObservationOpen, setEditObservationOpen] = useState(false);
  const [createObservationOpen, setCreateObservationOpen] = useState(false);
  const [observationToBeEdited, setObservationToBeEdited] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterNoteType, setFilterNoteType] = useState("past");
  const [customDateRange, setCustomDateRange] = useState({ fromDate: '', toDate: '' });
  const [debouncedDateRange, setDebouncedDateRange] = useState({ fromDate: '', toDate: '' });
  const { patient } = usePatient();
  const { user } = useUser();
  const dateRangeTimeoutRef = useRef(null);

  // Debounce date range changes to prevent multiple API calls
  useEffect(() => {
    if (dateRangeTimeoutRef.current) {
      clearTimeout(dateRangeTimeoutRef.current);
    }

    dateRangeTimeoutRef.current = setTimeout(() => {
      setDebouncedDateRange(customDateRange);
    }, 800); // Wait 800ms after user stops selecting dates

    return () => {
      if (dateRangeTimeoutRef.current) {
        clearTimeout(dateRangeTimeoutRef.current);
      }
    };
  }, [customDateRange]);

  // Calculate date range based on filter type or custom range
  const { fromDate, toDate } = useMemo(() => {
    // If custom date range is set, use it
    if (debouncedDateRange.fromDate || debouncedDateRange.toDate) {
      return {
        fromDate: debouncedDateRange.fromDate || null,
        toDate: debouncedDateRange.toDate || null
      };
    }

    if (filterNoteType === "isImp") {
      return { fromDate: null, toDate: null }; // Fetch all for important filter
    }
    
    const today = moment();
    const toDate = today.format("YYYY-MM-DD");
    let fromDate = null;

    if (filterNoteType === "today") {
      fromDate = today.format("YYYY-MM-DD");
    } else if (filterNoteType === "yesterday") {
      const yesterday = today.clone().subtract(1, "day");
      fromDate = yesterday.format("YYYY-MM-DD");
    } else if (filterNoteType === "past") {
      const sevenDaysAgo = today.clone().subtract(7, "days");
      fromDate = sevenDaysAgo.format("YYYY-MM-DD");
    }

    return { fromDate, toDate };
  }, [filterNoteType, debouncedDateRange]);

  const {
    data: notes,
    isLoading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ["Observation", patient?.id, fromDate, toDate],
    queryFn: () => listNotesApi(patient?.id, fromDate, toDate),
    enabled: !!patient?.id,
    refetchOnWindowFocus: false,
    keepPreviousData: true, // Keep previous data while fetching new data
    staleTime: 1000, // Consider data fresh for 1 second to prevent rapid refetches
  });

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearFilter = () => {
    setSearchTerm("");
    setFilterNoteType("all");
    setCustomDateRange({ fromDate: '', toDate: '' });
  };

  const handleDateRangeChange = (newRange) => {
    setCustomDateRange(newRange);
    // Clear preset filter when using custom date range
    if (newRange.fromDate || newRange.toDate) {
      setFilterNoteType('');
    }
  };

  // Filter notes by search term and important flag (date filtering is now done by API)
  const filteredNotes = useMemo(() => {
    if (!notes) return [];
    
    if (filterNoteType === "isImp") {
      return notes.filter((item) => item?.isImp === true);
    }
    
    // Only filter by search term, date filtering is handled by API
    return notes.filter((item) =>
      item.text.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [notes, filterNoteType, searchTerm]);

  // Show loading only on initial load, not when refetching
  if (isLoading && !notes) {
    return <p className="p-5">Loading observations...</p>;
  }

  if (isError) {
    return <p className="text-red-500 p-5">Error occurred while loading observations</p>;
  }

  const handleNoteFilterClick = (filterType) => {
    setFilterNoteType(filterType);
  };

  return (
    <>
      <div className="pb-20 xs:pb-0">
        <div className="flex items-end flex-wrap justify-end xs:justify-end xs:space-y-0">
          <div className="flex items-center">
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleSearch={handleSearch}
              handleNoteFilterClick={handleNoteFilterClick}
              handleClearFilter={handleClearFilter}
              filterNoteType={filterNoteType}
              dateRange={customDateRange}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>

          <div className="flex sm:mb-0">
            <button
              onClick={() => setCreateObservationOpen(true)}
              disabled={
                user?.role !== "Kare Admin" &&
                user?.role !== "Kare Giver"
              }
              className={`p-2 sm:p-3 ${
                user?.role !== "Kare Admin" &&
                user?.role !== "Kare Giver"
                  ? "flex items-center gap-2 rounded-full bg-[#ED7117] opacity-30"
                  : "flex items-center gap-2 rounded-full bg-[#ED7117]"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-8 h-8 sm:w-11 sm:h-11"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18 12.75H6c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h12c.41 0 .75.34.75.75s-.34.75-.75.75Z"
                  fill="#fff"
                ></path>
                <path
                  d="M12 18.75c-.41 0-.75-.34-.75-.75V6c0-.41.34-.75.75-.75s.75.34.75.75v12c0 .41-.34.75-.75.75Z"
                  fill="#fff"
                ></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Show loading indicator while fetching but keep data visible */}
        {isFetching && notes && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm text-blue-700">Updating observations...</span>
          </div>
        )}

        {filteredNotes?.length === 0 ? (
          <p className="text-[#ff0000] p-5">
            {isFetching ? 'Loading observations...' : 'No Observations found.'}
          </p>
        ) : (
            filteredNotes
              ?.sort((a, b) => {
                const timestampA = getCorrectDateTimeString(a.ntDate, a.ntTime)
                const timestampB = getCorrectDateTimeString(b.ntDate, b.ntTime)
                if (moment(timestampA).isSame(timestampB)) {
                  return a.id > b.id ? -1 : 1 // because seconds are not there so we are comparing SQL id
                } else {
                  return moment(timestampA).isBefore(timestampB) ? 1 : -1
                }
              }
              )
              ?.map((note, i) => (
                <Observation
                  key={note._id || note.id || i}
                  id={i}
                  note={note}
                  setEditObservationOpen={setEditObservationOpen}
                  setObservationToBeEdited={setObservationToBeEdited}
                />
              ))
        )}
      </div>
      <ReuseableBlurModel
        open={editObservationOpen}
        setOpen={setEditObservationOpen}
      >
        <EditObservationForm
          observation={observationToBeEdited}
          setOpen={setEditObservationOpen}
        />
      </ReuseableBlurModel>
      <ReuseableBlurModel
        open={createObservationOpen}
        setOpen={setCreateObservationOpen}
      >
        <AddObservationForm
          setOpen={setCreateObservationOpen}
          observationsRef={observationsRef}
        />
      </ReuseableBlurModel>
    </>
  );
}

export default ObservationBlock;
